/**
 * Per-employee live screen store.
 *
 * Maintains a Map<employeeId, ScreenNewEvent> that updates when the
 * server pushes `screen:new`. Components subscribe via `useScreen()`.
 *
 * Pattern: tiny Zustand-free store using React Context + useSyncExternalStore
 * to avoid pulling in zustand for one feature.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { SOCKET_EVENTS, type ScreenNewEvent } from '@monitor/shared'
import { subscribe } from '@/lib/socket'

interface LiveState {
  latestScreen: ScreenNewEvent | null
  isOnline: boolean
  lastHeartbeat: string | null
}

const listeners = new Set<() => void>()
const state = new Map<number, LiveState>()

// Module-level sentinel: when no entry exists for an employee, getSnapshot
// must return a STABLE reference. Returning a fresh object literal each call
// would make useSyncExternalStore think the snapshot changed every render →
// infinite re-render loop → blank screen.
const EMPTY_STATE: LiveState = Object.freeze({
  latestScreen: null,
  isOnline: false,
  lastHeartbeat: null,
}) as LiveState

function emit() {
  for (const l of listeners) l()
}

function getSnapshot(employeeId: number): LiveState {
  return state.get(employeeId) ?? EMPTY_STATE
}

function setLatestScreen(event: ScreenNewEvent) {
  // Important: a SCREEN_NEW with `deduped: true` is a cache replay sent on
  // dashboard connect (see socket-io.ts: sendLatestSnapshots) so the freshly
  // loaded FE shows the latest known frame. It does NOT mean the agent is
  // alive right now -- the frame could be hours old if the NV was shut
  // down. Setting isOnline here would mark every NV "online" on F5 even
  // after a power outage.
  //
  // isOnline / lastHeartbeat are owned by AGENT_ONLINE / AGENT_OFFLINE
  // events. If we have no AGENT_OFFLINE entry yet, default to "offline
  // until proven online" -- same behavior as if the user opened a brand
  // new tab with no socket history.
  const prev = state.get(event.employeeId) ?? EMPTY_STATE
  state.set(event.employeeId, {
    latestScreen: event,
    // Preserve whatever online state the agent-online/offline events set.
    // If neither fired yet, default to false so the FE doesn't claim an
    // offline NV is online just because we replayed its last screenshot.
    isOnline: prev.isOnline,
    lastHeartbeat: prev.lastHeartbeat,
  })
  emit()
}

function setOnline(employeeId: number, lastHeartbeat: string) {
  const prev = state.get(employeeId) ?? EMPTY_STATE
  state.set(employeeId, { latestScreen: prev.latestScreen, isOnline: true, lastHeartbeat })
  emit()
}

function setOffline(employeeId: number, lastHeartbeat: string | null) {
  const prev = state.get(employeeId) ?? EMPTY_STATE
  state.set(employeeId, { latestScreen: prev.latestScreen, isOnline: false, lastHeartbeat })
  emit()
}

/** Mount once at app root to bootstrap subscriptions. */
export function useScreenStoreInit(): void {
  useEffect(() => {
    const unsubScreen = subscribe<ScreenNewEvent>(SOCKET_EVENTS.SCREEN_NEW, setLatestScreen)
    const unsubOnline = subscribe<{ employeeId: number; lastHeartbeat: string }>(
      SOCKET_EVENTS.AGENT_ONLINE,
      (e) => setOnline(e.employeeId, e.lastHeartbeat),
    )
    const unsubOffline = subscribe<{ employeeId: number; lastHeartbeat: string | null }>(
      SOCKET_EVENTS.AGENT_OFFLINE,
      (e) => setOffline(e.employeeId, e.lastHeartbeat),
    )
    return () => {
      unsubScreen()
      unsubOnline()
      unsubOffline()
    }
  }, [])
}

/** Subscribe to the live screen state for one employee. */
export function useScreen(employeeId: number): LiveState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => getSnapshot(employeeId),
    () => getSnapshot(employeeId),
  )
}
